import React, { useState } from 'react';
import axios from 'axios';
import { Search, Database, Plus, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FoodDatabaseSearch = ({ token }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [foods, setFoods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFood, setSelectedFood] = useState(null);

  const searchFoods = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/food/search?query=${encodeURIComponent(searchQuery)}&limit=20`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFoods(response.data.foods || []);
    } catch (error) {
      console.error('Error searching food database:', error);
      alert('Failed to search food database');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchFoods();
    }
  };

  return (
    <div className="space-y-4" data-testid="food-database-search">
      {/* Search Section */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
        <h2 className="text-lg font-bold mb-2 flex items-center gap-2">
          <Database size={20} className="text-[#FF6600]" />
          Food Database
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          {t('foodDb.subtitle')}
        </p>
        
        <div className="flex gap-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('foodDb.searchPlaceholder')}
            className="flex-1 p-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
            data-testid="search-input"
          />
          <button
            onClick={searchFoods}
            disabled={loading}
            className="px-4 py-2 bg-[#FF6600] text-white rounded-lg hover:bg-[#E55C00] disabled:opacity-50"
            data-testid="search-btn"
          >
            <Search size={20} />
          </button>
        </div>
      </div>

      {/* Results */}
      {foods.length > 0 && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          <h3 className="font-bold mb-4">Search Results ({foods.length} items)</h3>
          <div className="space-y-3">
            {foods.map((food, idx) => (
              <div
                key={idx}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-[#FF6600] transition-all cursor-pointer"
                onClick={() => setSelectedFood(selectedFood?.name === food.name ? null : food)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-bold">{food.name}</h4>
                      {food.brand && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                          {food.brand}
                        </span>
                      )}
                    </div>
                    {food.serving_size && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        Serving: {food.serving_size}
                      </p>
                    )}
                    
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('foodDb.calories')}</div>
                        <div className="text-sm font-semibold text-[#FF6600]">
                          {Math.round(food.calories)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('foodDb.protein')}</div>
                        <div className="text-sm font-semibold">
                          {Math.round(food.protein || 0)}g
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('foodDb.carbs')}</div>
                        <div className="text-sm font-semibold">
                          {Math.round(food.carbs || 0)}g
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{t('foodDb.fats')}</div>
                        <div className="text-sm font-semibold">
                          {Math.round(food.fats || 0)}g
                        </div>
                      </div>
                    </div>

                    {selectedFood?.name === food.name && food.micronutrients && (
                      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <h5 className="text-sm font-semibold mb-2 flex items-center gap-1">
                          <Info size={14} />
                          Additional Nutrients
                        </h5>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                          {food.micronutrients.fiber && (
                            <div className="text-gray-600 dark:text-gray-300">
                              Fiber: {food.micronutrients.fiber}g
                            </div>
                          )}
                          {food.micronutrients.sodium && (
                            <div className="text-gray-600 dark:text-gray-300">
                              Sodium: {food.micronutrients.sodium}mg
                            </div>
                          )}
                          {food.micronutrients.sugar && (
                            <div className="text-gray-600 dark:text-gray-300">
                              Sugar: {food.micronutrients.sugar}g
                            </div>
                          )}
                          {food.micronutrients.vitamin_c && (
                            <div className="text-gray-600 dark:text-gray-300">
                              Vitamin C: {food.micronutrients.vitamin_c}mg
                            </div>
                          )}
                          {food.micronutrients.calcium && (
                            <div className="text-gray-600 dark:text-gray-300">
                              Calcium: {food.micronutrients.calcium}mg
                            </div>
                          )}
                          {food.micronutrients.iron && (
                            <div className="text-gray-600 dark:text-gray-300">
                              Iron: {food.micronutrients.iron}mg
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    className="ml-3 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-[#FF6600] hover:text-[#FF6600]"
                    onClick={(e) => {
                      e.stopPropagation();
                      alert('Quick add feature coming soon! For now, use the meal scanner to log foods.');
                    }}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-8">
          <div className="text-gray-500 dark:text-gray-400">{t('foodDb.searching')}...</div>
        </div>
      )}

      {!loading && foods.length === 0 && searchQuery && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm text-center">
          <Database size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">{t('foodDb.noResults')}</p>
        </div>
      )}
    </div>
  );
};

export default FoodDatabaseSearch;
